// URL scheme guard — shared render-side defense for RFC-0010 §2/§3 (PD-0010).
//
// Every anchor `href` and image `src` the portal renders originates from
// untrusted repository content (Markdown bodies, manifest `links[]`). This
// helper is the single source of truth for "is this URL safe to emit as a
// clickable/loadable attribute": only absolute `http(s)` URLs and genuinely
// relative references pass; every dangerous scheme (`javascript:`, `data:`,
// `vbscript:`, `file:`, `mailto:`, …) and the scheme-relative `//host` bypass
// are rejected.
//
// It is deliberately consumed by BOTH render surfaces independently:
//   - the Markdown-body sanitize pipeline (`rehypeGuardUrls`, sanitize-schema.ts)
//   - the metadata-panel link component (`MetadataLinksList`)
// so neither trusts its upstream validation alone (RFC-0010 §3, defense-in-depth
// atop RFC-0003's parse-time allowlist and the DB `CHECK`).

const SAFE_ABSOLUTE_PROTOCOLS = new Set(['http:', 'https:']);

/**
 * Returns true only for URLs safe to render as an anchor `href` or image `src`:
 * absolute `http`/`https` URLs, or relative references (no scheme). Absolute
 * non-http(s) schemes and scheme-relative `//host` URLs return false.
 *
 * Relative references are permitted because in-repo Markdown links/images are
 * relative and get resolved/rewritten downstream (RFC-0010 §7 `LinkResolver`,
 * Phase 3) — they carry no scheme and therefore cannot be an XSS vector.
 */
export function isSafeHttpUrl(value: string | null | undefined): boolean {
  if (typeof value !== 'string') return false;

  const trimmed = value.trim();
  if (trimmed === '') return false;

  // Scheme-relative URLs (`//evil.com`) inherit the page's scheme and slip past
  // a naive "no `:` before `/`" protocol check — reject explicitly.
  if (trimmed.startsWith('//')) return false;

  // The WHATWG URL parser strips embedded tab/newline/CR, so obfuscated schemes
  // like `java\tscript:` normalize back to `javascript:` and are caught by the
  // protocol check below rather than slipping through the relative branch.
  let parsed: URL;
  try {
    parsed = new URL(trimmed);
  } catch {
    // Not an absolute URL → a relative reference. No scheme, hence safe.
    return true;
  }

  return SAFE_ABSOLUTE_PROTOCOLS.has(parsed.protocol);
}
